/**
 * RecipientActions Component
 *
 * Action buttons and menu for recipient card.
 * Handles verification action, test button, and actions menu.
 *
 * @module RecipientItem/RecipientActions
 */

import {
  ActionList,
  Box,
  Button,
  InlineStack,
  Popover,
  Spinner,
  Tooltip,
} from '@shopify/polaris';
import {
  DeleteIcon,
  EditIcon,
  EmailIcon,
  MenuHorizontalIcon,
  SendIcon,
} from '@shopify/polaris-icons';

import type { VerificationState } from './types';
import { ARIA_LABELS } from './constants';

export interface RecipientActionsProps {
  /** Recipient name for accessibility labels */
  recipientName: string;
  /** Whether the recipient is verified */
  isVerified: boolean;
  /** Current verification state */
  verificationState: VerificationState;
  /** Whether actions menu is open */
  showActionsMenu: boolean;
  /** Callback to toggle actions menu */
  onToggleActionsMenu: () => void;
  /** Callback when verify button is clicked */
  onVerify: () => void;
  /** Callback when test button is clicked */
  onTest: () => void;
  /** Callback when edit is clicked */
  onEdit: () => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
  /** Hide the verification button (when showing inline code input) */
  hideVerificationButton?: boolean;
}

/**
 * Recipient action buttons component (Desktop)
 *
 * Displays:
 * - Verification button (idle state)
 * - Test button (verified state)
 * - Loading spinner (sending/testing states)
 * - Actions menu (edit, delete)
 */
export function RecipientActions({
  recipientName,
  isVerified,
  verificationState,
  showActionsMenu,
  onToggleActionsMenu,
  onVerify,
  onTest,
  onEdit,
  onDelete,
  hideVerificationButton = false,
}: RecipientActionsProps) {
  // Render verification action based on state
  const renderVerificationAction = () => {
    if (hideVerificationButton) return null;

    switch (verificationState) {
      case 'idle':
        return (
          <Tooltip content="Send verification email">
            <Button
              size="slim"
              variant="secondary"
              onClick={onVerify}
              icon={EmailIcon}
              accessibilityLabel={`Verify ${recipientName}`}
            >
              Verify
            </Button>
          </Tooltip>
        );

      case 'sending':
        return (
          <Box paddingInline="200">
            <Spinner size="small" />
          </Box>
        );

      case 'awaiting_code':
      case 'error':
      case 'verifying':
      case 'verified':
        return null; // Handled by inline input or dropdown menu

      case 'testing':
        return (
          <Box paddingInline="200">
            <Spinner size="small" />
          </Box>
        );

      default:
        return null;
    }
  };

  // Build action list items
  const actionItems = [
    // Test action (only for verified recipients)
    ...(isVerified
      ? [
          {
            content: 'Send test email',
            icon: SendIcon,
            onAction: () => {
              onToggleActionsMenu();
              onTest();
            },
          },
        ]
      : []),
    // Edit action
    {
      content: 'Edit',
      icon: EditIcon,
      onAction: () => {
        onToggleActionsMenu();
        onEdit();
      },
    },
    // Delete action
    {
      content: 'Delete',
      icon: DeleteIcon,
      destructive: true,
      onAction: () => {
        onToggleActionsMenu();
        onDelete();
      },
    },
  ];

  return (
    <InlineStack gap="100" blockAlign="center">
      {/* Verification/Test action */}
      <div>{renderVerificationAction()}</div>

      {/* Actions Menu (Test, Edit, Delete) */}
      <Box opacity={isVerified ? undefined : '50'}>
        <Popover
          active={showActionsMenu}
          activator={
            <Tooltip content="More actions">
              <Button
                size="slim"
                variant="tertiary"
                icon={MenuHorizontalIcon}
                onClick={onToggleActionsMenu}
                accessibilityLabel={ARIA_LABELS.ACTIONS_MENU}
              />
            </Tooltip>
          }
          onClose={onToggleActionsMenu}
          preferredAlignment="right"
        >
          <ActionList items={actionItems} />
        </Popover>
      </Box>
    </InlineStack>
  );
}

/**
 * Mobile-specific actions menu
 *
 * Simplified menu for mobile devices with all actions in a popover.
 */
export interface MobileRecipientActionsProps {
  /** Whether the recipient is verified */
  isVerified: boolean;
  /** Current verification state */
  verificationState: VerificationState;
  /** Whether actions menu is open */
  showActionsMenu: boolean;
  /** Callback to toggle actions menu */
  onToggleActionsMenu: () => void;
  /** Callback when verify is clicked */
  onVerify: () => void;
  /** Callback when test is clicked */
  onTest: () => void;
  /** Callback when edit is clicked */
  onEdit: () => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
}

export function MobileRecipientActions({
  isVerified,
  verificationState,
  showActionsMenu,
  onToggleActionsMenu,
  onVerify,
  onTest,
  onEdit,
  onDelete,
}: MobileRecipientActionsProps) {
  // Build action list items for mobile
  const mobileActionItems = [
    // Verification actions based on state
    ...(verificationState === 'idle'
      ? [
          {
            content: 'Send verification email',
            icon: EmailIcon,
            onAction: () => {
              onToggleActionsMenu();
              onVerify();
            },
          },
        ]
      : []),
    ...(verificationState === 'verified'
      ? [
          {
            content: 'Send test email',
            icon: SendIcon,
            onAction: () => {
              onToggleActionsMenu();
              onTest();
            },
          },
        ]
      : []),
    // Edit action
    {
      content: 'Edit',
      icon: EditIcon,
      onAction: () => {
        onToggleActionsMenu();
        onEdit();
      },
    },
    // Delete action
    {
      content: 'Delete',
      icon: DeleteIcon,
      destructive: true,
      onAction: () => {
        onToggleActionsMenu();
        onDelete();
      },
    },
  ];

  return (
    <Popover
      active={showActionsMenu}
      activator={
        <Button
          size="slim"
          variant="tertiary"
          icon={MenuHorizontalIcon}
          onClick={onToggleActionsMenu}
          accessibilityLabel={ARIA_LABELS.ACTIONS_MENU}
        />
      }
      onClose={onToggleActionsMenu}
      preferredAlignment="right"
    >
      <ActionList items={mobileActionItems} />
    </Popover>
  );
}
